import type { PostHog } from "posthog-js";
import type { PosthogPlugin } from "@capawesome/capacitor-posthog";
import { detectPlatform } from "./Platform";

/**
 * PostHog 초기화/식별 래퍼. posthog-js import 를 이 파일 하나로 가둬서, 나중에
 * 수집 도구를 바꾸거나 끌 때 소비처(main.tsx, App.tsx)를 건드리지 않게 한다.
 *
 * posthog-js 는 무거운 SDK 라 `await import()` 로 동적 로드해 entry(초기) 청크에서
 * 빼낸다 — 로그인 화면 등 첫 화면엔 필요 없으므로 초기 임계 경로에서 제외하고,
 * 별도 vendor-posthog 청크(vite.config.ts manualChunks)로 나가 백그라운드/식별
 * 시점에 로드된다. 로드 완료 전에 도착한 identify/reset 호출은 loadPosthog() 의
 * 프로미스에 체이닝돼 로드 후 순서대로 반영된다.
 *
 * api_host 는 자체 도메인의 `/ingest` — server/core 의 modules/analytics/routes.ts
 * 가 eu.i.posthog.com(이벤트) / eu-assets.i.posthog.com(SDK 애셋)으로 그대로
 * 중계한다. 광고 차단기 우회 + 퍼스트파티 쿠키가 목적이고, 페이로드는 손대지
 * 않으므로 오토캡처·세션 리플레이·피처 플래그가 전부 그대로 동작한다.
 * SPA 는 웹/Electron/Capacitor 모두 배포 origin(dashboard.withtask.app)을 로드하므로
 * 상대경로 하나로 세 플랫폼이 다 커버된다(capacitor.config.json 의 server.url,
 * apps/desktop/src/main/env.ts 의 APP_URL).
 *
 * ui_host 는 툴바/리플레이 링크가 가리킬 PostHog 앱 주소 — 프록시 대상이 아니라
 * 실제 대시보드 도메인이라 별도로 준다. EU 리전 프로젝트라 eu.posthog.com 이다
 * (프록시 타겟 호스트는 server/core 의 modules/analytics/routes.ts 참고).
 */
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const PROXY_HOST = "/ingest";
const POSTHOG_UI_HOST = "https://eu.posthog.com";
// 네이티브 SDK(loadNativePosthog)의 ingest 대상. 웹뷰 posthog-js 는 상대경로 /ingest 로
// 자체 도메인 프록시를 타지만(위 PROXY_HOST), 웹뷰 밖 네이티브 코드는 상대경로를 못
// 써서 EU ingest(server/core modules/analytics/routes.ts 의 EVENT_HOST 와 동일)로 직결한다.
const POSTHOG_INGEST_HOST = "https://eu.i.posthog.com";
// 배포 채널(prod/nightly/develop). 단일 PostHog 프로젝트에서 채널을 가르는 environment
// 슈퍼 프로퍼티로 등록한다(서버측 shared/lib/analytics.ts 의 environment 와 짝). 값은
// 각 채널의 SPA 빌드가 VITE_PUBLIC_APP_ENV 로 박는다(nightly/develop 빌드가 오버라이드).
// 미설정인 PROD 빌드는 유일한 배포 채널인 prod 로 본다 — dev 빌드는 애초에 posthog 를
// 로드하지 않으므로(아래 isDevBuild 가드) 이 폴백이 dev 를 prod 로 오염시키지 않는다.
const APP_ENV = import.meta.env.VITE_PUBLIC_APP_ENV ?? "prod";

// 로드된 posthog 인스턴스(로드 전/미수집 환경에서는 null). captureTestError 처럼
// 동기 반환이 필요한 곳은 이 값으로 로드 여부를 판단한다.
let posthog: PostHog | null = null;
// posthog-js 동적 로드 + init 을 1회만 수행하는 프로미스. dev 빌드/키 미설정이면
// null 로 resolve 돼 모든 호출이 조용히 no-op 이 된다.
let loadPromise: Promise<PostHog | null> | null = null;

function loadPosthog(): Promise<PostHog | null> {
  if (loadPromise) return loadPromise;
  // 개발 빌드인지 — dev 이벤트로 프로덕션 프로젝트를 오염시키지 않도록 건너뛴다.
  const isDevBuild = !import.meta.env.PROD;
  // 프로젝트 키가 설정되지 않았는지 (Infisical/.env 미설정 환경)
  const isKeyMissing = !POSTHOG_KEY;
  if (isDevBuild || isKeyMissing) {
    loadPromise = Promise.resolve(null);
    return loadPromise;
  }
  loadPromise = import("posthog-js").then(({ default: ph }) => {
    ph.init(POSTHOG_KEY, {
      api_host: PROXY_HOST,
      ui_host: POSTHOG_UI_HOST,
      defaults: "2025-05-24",
      // 익명 방문자까지 person 프로필을 만들지 않는다 — identify 된 유저만 프로필을
      // 가지고, 그 전 이벤트는 익명 이벤트로 남는다.
      person_profiles: "identified_only",
      // 브라우저 런타임 예외를 PostHog Error tracking 으로 자동 전송한다 —
      // window.onerror(동기 에러) + unhandledrejection(await/Promise 미처리)을
      // posthog-js 가 후킹한다. defaults 프리셋에는 안 들어 있어 명시로 켠다.
      // 단, React 렌더 중 던져진 에러는 상위 ErrorBoundary 가 가로채므로 여기로
      // 안 온다(React 19 는 바운더리가 잡은 에러를 window 로 다시 던지지 않는다) —
      // 그건 components/ErrorBoundary.tsx 가 captureException 으로 따로 올린다.
      capture_exceptions: true,
    });
    // 이후 모든 이벤트/오토캡처/예외/세션 리플레이에 environment 를 자동으로 붙인다 —
    // 단일 프로젝트에서 채널(prod/nightly/develop)을 필터로 가르기 위한 슈퍼 프로퍼티.
    ph.register({ environment: APP_ENV });
    posthog = ph;
    return ph;
  });
  return loadPromise;
}

// ── 네이티브(Capacitor) 예외/에러 보완 ────────────────────────────────────────
// 네이티브 앱(android/ios)도 dashboard.withtask.app 을 웹뷰로 로드하므로 위 posthog-js
// 가 웹 레이어의 이벤트·오토캡처·JS 예외를 그대로 수집한다. 다만 웹뷰 JS 는 네이티브
// 레이어(Kotlin/Swift)의 크래시를 볼 수 없다 — @capawesome/capacitor-posthog 네이티브
// SDK 를 함께 띄워 autoCaptureExceptions 로 그 사각지대만 메운다. 제품 이벤트/오토캡처/
// 렌더 예외(captureException)는 계속 posthog-js 가 담당한다(이중 집계 방지) — 여기서는
// setup + 네이티브 예외 자동캡처 + 유저 귀속(identify/reset)만 미러한다.
// 네이티브 코드는 웹뷰 밖이라 상대경로 /ingest 프록시(posthog-js 가 쓰는 PROXY_HOST)를
// 쓸 수 없어 EU ingest 로 직결한다. 네이티브 빌드는 VITE_PUBLIC_APP_ENV 를 주입하지
// 않으므로 APP_ENV 는 "prod" 로 떨어진다(네이티브에는 nightly/develop 채널이 없다 —
// 난수 도메인은 웹 전용). posthog-js 와 같은 environment 슈퍼 프로퍼티를 걸어 맞춘다.
let nativePosthogPromise: Promise<PosthogPlugin | null> | null = null;

function loadNativePosthog(): Promise<PosthogPlugin | null> {
  if (nativePosthogPromise) return nativePosthogPromise;
  const platform = detectPlatform();
  const isNativeShell = platform === "android" || platform === "ios";
  const isDevBuild = !import.meta.env.PROD;
  // 네이티브 셸이 아니거나(웹/데스크톱), dev 빌드/키 미설정이면 조용히 no-op —
  // 웹/데스크톱의 예외는 이미 posthog-js 가 잡고, dev 는 프로덕션 프로젝트를 오염시키지
  // 않으려 건너뛴다(loadPosthog 의 가드와 동일한 기준).
  if (!isNativeShell || isDevBuild || !POSTHOG_KEY) {
    nativePosthogPromise = Promise.resolve(null);
    return nativePosthogPromise;
  }
  nativePosthogPromise = import("@capawesome/capacitor-posthog")
    .then(async ({ Posthog }) => {
      await Posthog.setup({
        apiKey: POSTHOG_KEY,
        apiHost: POSTHOG_INGEST_HOST,
        // 네이티브 레이어의 unhandled 예외를 자동 수집한다(이 통합의 유일한 목적).
        autoCaptureExceptions: true,
      });
      await Posthog.register({ key: "environment", value: APP_ENV });
      return Posthog;
    })
    // setup 실패가 앱을 막지 않도록 삼킨다(관측 보조 단계) — 웹뷰 posthog-js 는 그대로 동작.
    .catch(() => null);
  return nativePosthogPromise;
}

export function initAnalytics(): void {
  // 렌더를 막지 않도록 로드는 백그라운드로만 시작한다(첫 페이지뷰/오토캡처는
  // 로드 완료 직후부터 잡힌다 — 콜드 스타트 몇백 ms 는 감수한다).
  void loadPosthog();
  // 네이티브 셸에서만 실제로 로드된다(그 외엔 no-op).
  void loadNativePosthog();
}

/** 세션이 확정되면 이벤트를 해당 유저에게 귀속시킨다(로그인 전 익명 이벤트도 병합된다). */
export function identifyUser(userId: string): void {
  void loadPosthog().then((ph) => ph?.identify(userId));
  // 네이티브 SDK 가 자동 수집한 예외도 같은 유저에게 귀속시킨다.
  void loadNativePosthog().then((np) => np?.identify({ distinctId: userId }));
}

/** 로그아웃/세션 무효 확인 시 — 다음 사용자가 이전 계정으로 귀속되지 않게 익명 ID를 새로 발급한다. */
export function resetAnalytics(): void {
  void loadPosthog().then((ph) => ph?.reset());
  void loadNativePosthog().then((np) => np?.reset());
}

/**
 * 설정 > 개발자모드의 "에러 테스트" 버튼용. PostHog Error tracking 에 잡히는지
 * 확인하려고 일부러 만든 에러를 보낸다 — 앱을 실제로 죽이지 않도록 throw 하지
 * 않고 captureException 으로만 올린다. label 은 그대로 이슈 제목이자 그룹핑 키가
 * 된다(제목이 다르면 새 이슈 → 알림도 매번 온다).
 *
 * 에러를 대시보드에서 세션 리플레이와 함께 보려고, 캡처 직전에 녹화를 강제로
 * 시작한다. startSessionRecording(true) 는 샘플링·linked_flag 같은 클라이언트
 * 게이팅을 무시하고 이 세션 녹화를 켜므로(disable_session_recording=false),
 * 그 뒤 captureException 이 붙이는 $session_id 가 실제 존재하는 리플레이를
 * 가리키게 된다. 단 프로젝트 레벨 세션 리플레이 토글이 꺼져 있으면 원격 config
 * 가 막아서 여기서 강제해도 녹화되지 않는다(그건 PostHog 대시보드 설정).
 *
 * 수집이 꺼져 있으면(dev 빌드/키 미설정) false 를 돌려주고 아무것도 보내지 않는다 —
 * 호출부(설정 > 개발자모드)가 이걸로 실패 토스트를 띄운다.
 */
/**
 * React 렌더 중 던져진 에러를 PostHog Error tracking 으로 올린다 —
 * components/ErrorBoundary.tsx 의 componentDidCatch 에서 호출한다. init 의
 * capture_exceptions 는 window.onerror/unhandledrejection(=바운더리가 못 잡는
 * 비동기·이벤트 핸들러·rejection)만 잡고, React 19 는 바운더리가 삼킨 에러를
 * window 로 다시 던지지 않으므로 렌더 에러는 여기서 명시적으로 보낸다(중복 아님).
 * 로드 전/미수집(dev·키 미설정)이면 posthog 가 null 이라 조용히 버린다.
 */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  void loadPosthog().then((ph) => ph?.captureException(error, context));
}

export function captureTestError(label: string): boolean {
  // posthog 가 아직 로드/초기화되지 않았으면(dev/키 미설정/로딩 중) 실패로 반환한다 —
  // 설정 > 개발자모드는 부팅 한참 뒤라 프로덕션에서는 항상 로드가 끝나 있다.
  if (!posthog) return false;
  // 이 세션이 샘플링 등으로 녹화에서 빠졌더라도 리플레이가 남도록 강제로 켠다.
  posthog.startSessionRecording(true);
  const error = new Error("설정 > 개발자모드에서 보낸 테스트 에러");
  // 라벨을 exception type 으로 올린다 — PostHog 이슈 제목이 라벨 그대로가 된다.
  error.name = label;
  posthog.captureException(error, {
    is_test: true,
    test_label: label,
    source: "settings/developer-mode",
    // 그룹핑 키도 라벨로 못박는다. 자동 fingerprint 는 exception type + 스택 프레임
    // 기준이라, 이걸 안 주면 항상 이 함수의 같은 줄에서 난 에러로 뭉쳐 한 이슈가 된다 —
    // 이슈가 새로 안 생기니 PostHog 알림(Discord)도 첫 건 말고는 오지 않는다.
    // 서버 쪽 reportIssue(shared/lib/analytics.ts)와 같은 규칙이다.
    $exception_fingerprint: label,
  });
  return true;
}
